import { useState } from 'react';
import { Input } from '@/components/_untitled/base/input/input';
import { Radio, RadioGroup } from 'react-aria-components';
import type { KycStepProps } from '../../config/kycSteps.config';
import { StepFooter } from '../StepFooter';
import { prefixes, useTypedTranslation } from "@/locales";

export function AddressStep({ data, onComplete, onBack }: KycStepProps) {
  const [tGeneral, tAddress] = useTypedTranslation(prefixes('general', 'kyc.steps.address'));

  const [streetAddress, setStreetAddress] = useState(data.streetAddress || '');
  const [city, setCity] = useState(data.city || '');
  const [zipCode, setZipCode] = useState(data.zipCode || '');
  const [country, setCountry] = useState(data.country || '');
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [billingStreetAddress, setBillingStreetAddress] = useState(
    data.billingStreetAddress || ''
  );
  const [billingCity, setBillingCity] = useState(data.billingCity || '');
  const [billingZipCode, setBillingZipCode] = useState(data.billingZipCode || '');
  const [billingCountry, setBillingCountry] = useState(data.billingCountry || '');

  const canProceed =
    !!streetAddress &&
    !!city &&
    !!zipCode &&
    !!country &&
    (sameAsShipping ||
      (!!billingStreetAddress && !!billingCity && !!billingZipCode && !!billingCountry));

  const handleContinue = () => {
    onComplete({
      streetAddress,
      city,
      zipCode,
      country,
      billingStreetAddress: sameAsShipping ? streetAddress : billingStreetAddress,
      billingCity: sameAsShipping ? city : billingCity,
      billingZipCode: sameAsShipping ? zipCode : billingZipCode,
      billingCountry: sameAsShipping ? country : billingCountry,
    });
  };

  return (
    <div className="flex flex-col gap-6">

      <div className="flex flex-col gap-4">
        <Input
            label={ tAddress('streetAddress') }
          value={streetAddress}
          onChange={(value) => setStreetAddress(value)}
            aria-label={ tAddress('streetAddress') }
          isRequired
        />
        <div className="flex gap-6">
          <Input
              label={ tAddress('city') }
            value={city}
            onChange={(value) => setCity(value)}
              aria-label={ tAddress('city') }
            isRequired
            className="flex-1"
          />
          <Input
              label={ tAddress('zipCode') }
            value={zipCode}
            onChange={(value) => setZipCode(value)}
              aria-label={ tAddress('zipCode') }
            isRequired
            className="flex-1"
          />
        </div>
        <Input
            label={ tAddress('country') }
          value={country}
          onChange={(value) => setCountry(value)}
            aria-label={ tAddress('country') }
          isRequired
        />
      </div>

      <div className="h-px bg-border-secondary w-full" />

      <div className="flex flex-col gap-5">
        <p className="text-md font-semibold text-tertiary">
          { tAddress('billingPreferences') }
        </p>

        <RadioGroup
          value={sameAsShipping ? 'same' : 'different'}
          onChange={(value) => setSameAsShipping(value === 'same')}
          aria-label={ tAddress('billingPreferences') }
          className="flex flex-col gap-5"
        >
          <Radio value="same" className="flex items-start gap-2 group cursor-pointer">
            <div className="flex items-center justify-center pt-0.5 shrink-0">
              <div className="size-4 rounded-full border border-secondary_alt group-data-[selected]:border-brand-solid group-data-[selected]:bg-brand-solid flex items-center justify-center">
                <div className="size-[5px] rounded-full bg-white opacity-0 group-data-[selected]:opacity-100" />
              </div>
            </div>
            <p className="text-sm text-tertiary">{ tAddress('sameAsShipping') }</p>
          </Radio>

          <Radio value="different" className="flex items-start gap-2 group cursor-pointer">
            <div className="flex items-center justify-center pt-0.5 shrink-0">
              <div className="size-4 rounded-full border border-secondary_alt group-data-[selected]:border-brand-solid group-data-[selected]:bg-brand-solid flex items-center justify-center">
                <div className="size-[5px] rounded-full bg-white opacity-0 group-data-[selected]:opacity-100" />
              </div>
            </div>
            <p className="text-sm text-tertiary">{ tAddress('differentBilling') }</p>
          </Radio>
        </RadioGroup>
      </div>

      {!sameAsShipping && (
        <div className="flex flex-col gap-[18px]">
          <Input
              label={ tAddress('streetAddress') }
            value={billingStreetAddress}
            onChange={(value) => setBillingStreetAddress(value)}
              aria-label={ tAddress('billingStreetAddress') }
            isRequired
          />
          <div className="flex gap-6">
            <Input
                label={ tAddress('city') }
              value={billingCity}
              onChange={(value) => setBillingCity(value)}
                aria-label={ tAddress('billingCity') }
              isRequired
              className="flex-1"
            />
            <Input
                label={ tAddress('zipCode') }
              value={billingZipCode}
              onChange={(value) => setBillingZipCode(value)}
                aria-label={ tAddress('billingZipCode') }
              isRequired
              className="flex-1"
            />
          </div>
          <Input
              label={ tAddress('country') }
            value={billingCountry}
            onChange={(value) => setBillingCountry(value)}
              aria-label={ tAddress('billingCountry') }
            isRequired
          />
        </div>
      )}

      <StepFooter
        primary={{
          label: tGeneral('continue'),
          onClick: handleContinue,
          disabled: !canProceed
        }}
        back={{ show: true, onClick: onBack }}
      />
    </div>
  );
}
